import React, { createContext, useContext } from "react";

import { embedMode, type EmbedMode } from "./embedMode";

/**
 * Whether this tree is rendering embedded, and how (#11021 frame, #9163 app).
 *
 * With no provider the answer is the document's own detection, so the whole
 * application answers correctly with **no provider anywhere** — embed mode is
 * a property of the document, not of a subtree. `EmbedProvider` exists so
 * tests (and only tests) can render a mode on demand.
 *
 * What it changes is presentational: navigation is removed in both modes, and
 * in frame mode the frame stays on the record (`EmbedFrame`). It is not a
 * permission and not a security boundary — see `embedMode.ts`.
 */
const EmbedContext = createContext<EmbedMode | null | undefined>(undefined);

export const EmbedProvider: React.FC<{
  children: React.ReactNode;
  /** `true` (the default) is frame mode, `false` not embedded. */
  value?: boolean;
}> = ({ children, value = true }) => (
  <EmbedContext.Provider value={value ? "frame" : null}>
    {children}
  </EmbedContext.Provider>
);

/** The mode this tree renders in, or null outside any embed. */
export function useEmbedMode(): EmbedMode | null {
  const provided = useContext(EmbedContext);
  return provided === undefined ? embedMode() : provided;
}

/** True in both modes: the application navigation is absent in each. */
export function useIsEmbedded(): boolean {
  return useEmbedMode() !== null;
}

/**
 * A panel on another product's page (#11021): the host page owns every piece
 * of chrome, page headers included.
 */
export function useIsFramed(): boolean {
  return useEmbedMode() === "frame";
}

/**
 * Inside the Finpilot Notetaker app's web view (#9163): the user's own app,
 * with page headers and an up control but no application navigation.
 */
export function useIsAppShell(): boolean {
  return useEmbedMode() === "app";
}
